import Bugsnag from "@bugsnag/js";
import axios from "axios";
import dayjs from "dayjs";
import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import styled from "styled-components";
import { BooleanParam, useQueryParam } from "use-query-params";

import { addToast } from "../../../Store/Actions";
import axiosInstance from "../../../axiosInstance";
import { trackEvent } from "../../../helperFunctions";
import URL from "../../../urls";
import { Button } from "../../common/Buttons";
import FlexBox from "../../common/FlexBox";
import { Body2, H3, Support } from "../../common/Headings";
import { Modal } from "../../common/Modal";
import { DAVYS_GREY_700 } from "../../common/colors";
import AccountAvatar from "./AccountAvatar";

const Container = styled(FlexBox)`
  flex-direction: column;
  row-gap: 1.5rem;
  padding: 1.5rem;
`;

const Details = styled(FlexBox)`
  column-gap: 0.5rem;
`;

const AccountDetails = styled(FlexBox)`
  flex: 1;
  flex-direction: column;
  row-gap: 0.25rem;
`;

const RemoveAccountModal = ({ userAccount, toggleRemoveAccountModal }) => {
  const dispatch = useDispatch();

  const user = useSelector(state => state.auth.user);
  const { googleClientId, googleClientSecret } = useSelector(state => ({
    googleClientId: state.auth.googleAuth.clientId,
    googleClientSecret: state.auth.googleAuth.clientSecret,
  }));

  const [deletingAccount, setDeletingAccount] = useState(false);

  const [, setReloadLinkedAccounts] = useQueryParam(
    "reloadLinkedAccounts",
    BooleanParam
  );

  const { user_info, credentials } = userAccount || {};

  const { name, email } = user_info || {};

  const validateAccessToken = () => {
    const expirationTime = userAccount?.credentials?.expirationTime;
    return dayjs().isBefore(expirationTime);
  };

  const refreshAccessToken = async () => {
    try {
      const refreshToken = userAccount?.credentials?.refresh_token;
      if (!refreshToken) {
        Bugsnag.notify(
          "Google Refresh Token Error: refresh_token missing in RemoveAccountModal"
        );
        return;
      }

      if (!googleClientId || !googleClientSecret) {
        return;
      }

      // update creds if access token expires
      const response = await axios.post(URL.authToken, {
        client_id: googleClientId,
        client_secret: googleClientSecret,
        refresh_token: refreshToken,
        grant_type: "refresh_token",
      });
      return response.data.access_token;
    } catch (error) {
      Bugsnag.notify(error);
      // in case refresh token is expired or consent is revoked manually through google account
      // api returns 400 status code
      if (error?.response?.status === 400) {
        removeAccount();
        dispatch(
          addToast({
            msg: "Calendar session expired. Please relink account.",
            appearance: "error",
            autoDismiss: true,
          })
        );
      }
    }
  };

  const revokeConsent = async () => {
    let token = credentials?.access_token;
    const validated = validateAccessToken();
    // refresh access token if it's expired
    if (!validated) token = await refreshAccessToken();
    if (!token) return;
    try {
      await axios.post(URL.revokeConsent, null, { params: { token } });
      return true;
    } catch (error) {
      Bugsnag.notify(error);
      dispatch(
        addToast({
          msg: "Error removing account",
          appearance: "error",
          autoDismiss: true,
        })
      );
    }
  };

  const removeAccount = async () => {
    try {
      setDeletingAccount(true);
      const revoked = await revokeConsent();
      if (!revoked) return;
      await axiosInstance.delete(URL.removeAccount, {
        data: { email },
      });
      trackEvent({
        event: "twa_db_calendar_remove_account",
        payload: {
          provider_name: user?.name,
          provider_uuid: user?.uuid,
          provider_type: user?.usertype,
        },
      });
      dispatch(
        addToast({
          msg: "Account removed successfully",
          appearance: "success",
          autoDismiss: true,
        })
      );
      toggleRemoveAccountModal();
      setReloadLinkedAccounts(1, "replaceIn");
    } catch (error) {
      Bugsnag.notify(error);
      dispatch(
        addToast({
          msg: "Error removing account",
          appearance: "error",
          autoDismiss: true,
        })
      );
    } finally {
      setDeletingAccount(false);
    }
  };

  return (
    <Modal XXS borderRadius="1rem" width="27rem" height="auto">
      <Container>
        <FlexBox column rowGap="1rem">
          <H3 bold>
            Are you sure you want to remove the following google account?
          </H3>
          <Details>
            <AccountAvatar name={name} />
            <AccountDetails>
              <Body2 bold>{name}</Body2>
              <Support color={DAVYS_GREY_700}>{email}</Support>
            </AccountDetails>
          </Details>
          <Body2>
            Once removed, you’ll not be able to view the google calendar events
            for this account on your dashboard.
          </Body2>
        </FlexBox>
        <FlexBox justify="space-between" align="center">
          <Button outline tertiary onClick={toggleRemoveAccountModal}>
            Go Back
          </Button>
          <Button onClick={removeAccount} disabled={deletingAccount}>
            YES, DELETE
          </Button>
        </FlexBox>
      </Container>
    </Modal>
  );
};

export default RemoveAccountModal;
